import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Box, CircularProgress, IconButton, Menu, MenuItem, Stack, Typography } from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import ReplyRoundedIcon from '@mui/icons-material/ReplyRounded';
import ThumbUpRoundedIcon from '@mui/icons-material/ThumbUpRounded';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getQaNotifications, markAllQaNotificationsRead, markQaNotificationRead } from '../../services/qaNotifications';

const TYPE_LABELS = {
  NEW_COMMENT: 'answered your question',
  NEW_REPLY: 'replied to your answer',
  QUESTION_VOTED: 'voted on your question',
  COMMENT_VOTED: 'voted on your answer',
};

const TYPE_ICONS = {
  NEW_COMMENT: ForumRoundedIcon,
  NEW_REPLY: ReplyRoundedIcon,
  QUESTION_VOTED: ThumbUpRoundedIcon,
  COMMENT_VOTED: ThumbUpRoundedIcon,
};

function describe(notification) {
  const label = TYPE_LABELS[notification.type] || 'interacted with your content';
  if (notification.actor_count > 1) {
    return `${notification.actor_count} people ${label}`;
  }
  return `Someone ${label}`;
}

const MINUTE = 60;
const HOUR = MINUTE * 60;
const DAY = HOUR * 24;
const WEEK = DAY * 7;

function timeAgo(dateString) {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(dateString).getTime()) / 1000));
  if (seconds < MINUTE) return 'Just now';
  if (seconds < HOUR) return `${Math.floor(seconds / MINUTE)}m ago`;
  if (seconds < DAY) return `${Math.floor(seconds / HOUR)}h ago`;
  if (seconds < WEEK) return `${Math.floor(seconds / DAY)}d ago`;
  return new Date(dateString).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const data = await getQaNotifications(token);
      setNotifications(data.notifications ?? []);
      setUnreadCount(data.unreadCount ?? 0);
    } catch {
      // Non-fatal: bell just stays at its last known state.
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    if (!user) return undefined;
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications, user]);

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    setLoading(true);
    await fetchNotifications();
    setLoading(false);
  };

  const handleClose = () => setAnchorEl(null);

  const handleNotificationClick = async (notification) => {
    handleClose();
    if (!notification.read_at) {
      try {
        const token = await user.getIdToken();
        await markQaNotificationRead(token, notification.id);
        setUnreadCount((count) => Math.max(0, count - 1));
        setNotifications((current) =>
          current.map((item) => (item.id === notification.id ? { ...item, read_at: new Date().toISOString() } : item)),
        );
      } catch {
        // Non-fatal: navigation still proceeds even if marking read failed.
      }
    }
    if (notification.link) navigate(notification.link);
  };

  const handleMarkAllRead = async (event) => {
    event.stopPropagation();
    try {
      const token = await user.getIdToken();
      await markAllQaNotificationsRead(token);
      setUnreadCount(0);
      setNotifications((current) =>
        current.map((item) => ({ ...item, read_at: item.read_at || new Date().toISOString() })),
      );
    } catch {
      // Non-fatal.
    }
  };

  if (!user) return null;

  return (
    <>
      <IconButton aria-label="Notifications" onClick={handleOpen} sx={{ color: 'text.primary' }}>
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          list: { sx: { p: 0 } },
          paper: {
            sx: {
              width: 340,
              maxHeight: 420,
              bgcolor: '#fff',
              boxShadow: '0 8px 28px rgba(19,62,135,0.16)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            },
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Notifications</Typography>
          {unreadCount > 0 ? (
            <Typography
              onClick={handleMarkAllRead}
              sx={{ fontSize: '0.8rem', color: 'primary.main', cursor: 'pointer' }}
            >
              Mark all read
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ overflowY: 'auto', minHeight: 0 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={20} />
            </Box>
          ) : notifications.length ? (
            notifications.map((notification) => {
              const Icon = TYPE_ICONS[notification.type] || ForumRoundedIcon;
              const isUnread = !notification.read_at;
              return (
                <MenuItem
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    whiteSpace: 'normal',
                    bgcolor: isUnread ? 'action.hover' : 'transparent',
                    py: 1.25,
                    alignItems: 'flex-start',
                  }}
                >
                  <Stack direction="row" spacing={1.25} sx={{ width: '100%' }} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        flexShrink: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: 'action.selected',
                        color: 'text.secondary',
                        mt: 0.25,
                      }}
                    >
                      <Icon sx={{ fontSize: 16 }} />
                    </Box>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: '0.9rem', fontWeight: isUnread ? 700 : 400 }}>
                        {describe(notification)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.25 }}>
                        {timeAgo(notification.updated_at || notification.created_at)}
                      </Typography>
                    </Box>
                    {isUnread ? (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'error.main',
                          flexShrink: 0,
                          mt: 0.75,
                        }}
                      />
                    ) : null}
                  </Stack>
                </MenuItem>
              );
            })
          ) : (
            <Box sx={{ px: 2, py: 3 }}>
              <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>No notifications yet.</Typography>
            </Box>
          )}
        </Box>
      </Menu>
    </>
  );
}

export default NotificationBell;
